import { useEffect, useState, type ReactElement } from 'react';
import { get } from '../shared/api/client';
import { useLocale, useMessages } from '../shared/i18n/locale';
import { formatTime } from '../shared/lib/format';
import { statusLabel, toneForOrder, toneVars } from '../shared/lib/status';

/**
 * Публичная страница отслеживания (FR-594, FR-559).
 *
 * Открывается получателем по ссылке из СМС, без авторизации. Состав ровно тот, что
 * отдаёт сервер, и ничего сверх: телефона курьера здесь нет (FR-596), позиция
 * показывается только пока заказ в пути (FR-595).
 *
 * Карта не отображается: нужен ключ Яндекс Maps JS API (Б-01), а подложка другого
 * поставщика запрещена правилом одного поставщика (A12). До ключа страница показывает
 * время последней известной позиции — это то, ради чего получатель её и открывает.
 *
 * Язык переключается кнопкой (FR-559): ссылка приходит на языке заказа, но открыть её
 * может кто угодно из семьи.
 */

interface TrackingView {
  readonly display_id: string;
  readonly external_number: string | null;
  readonly status: string;
  readonly promised_at: string | null;
  readonly eta_delivery_at: string | null;
  readonly delivery_window_from: string | null;
  readonly delivery_window_to: string | null;
  readonly courier: {
    readonly name: string;
    readonly position: { lat: number; lng: number } | null;
    readonly position_at: string | null;
  } | null;
  readonly address_short: string | null;
}

const REFRESH_MS = 30_000;

export function PublicTrackingPage({
  code,
  token,
}: {
  readonly code?: string;
  readonly token?: string;
}): ReactElement {
  const messages = useMessages();
  const { locale, setLocale } = useLocale();

  const [view, setView] = useState<TrackingView | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const path = token === undefined ? `/public/t/${code ?? ''}` : `/public/track/${token}`;

    const load = (): void => {
      void get<TrackingView>(path)
        .then((result) => {
          setView(result);
          setFailed(false);
        })
        .catch(() => setFailed(true));
    };

    load();

    // Обновление раз в полминуты: получатель держит страницу открытой, ожидая
    // курьера, и обязан видеть изменение статуса без перезагрузки.
    const timer = setInterval(load, REFRESH_MS);

    return () => clearInterval(timer);
  }, [code, token]);

  return (
    <main
      style={{
        maxWidth: '520px',
        margin: '0 auto',
        padding: 'var(--sp-6) var(--sp-4)',
        display: 'grid',
        gap: 'var(--sp-4)',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{messages.cabinet.nav.flow}</h1>
        <button
          type="button"
          onClick={() => setLocale(locale === 'ru' ? 'uz-Latn' : 'ru')}
          style={{
            background: 'none',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--r-md)',
            padding: 'var(--sp-1) var(--sp-3)',
          }}
        >
          {locale === 'ru' ? messages.languages.uzLatn : messages.languages.ru}
        </button>
      </header>

      {failed ? (
        <p style={{ color: 'var(--danger)' }}>{messages.cabinet.common.error}</p>
      ) : view === null ? (
        <p style={{ color: 'var(--text-muted)' }}>{messages.cabinet.common.loading}</p>
      ) : (
        <>
          <section
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-lg)',
              padding: 'var(--sp-4)',
              display: 'grid',
              gap: 'var(--sp-3)',
            }}
          >
            <span className="num" style={{ fontSize: 'var(--fs-heading)' }}>
              {view.external_number ?? view.display_id}
            </span>

            <span
              style={{
                ...toneVars(toneForOrder(view.status)),
                padding: '2px var(--sp-2)',
                borderRadius: 'var(--r-sm)',
                justifySelf: 'start',
              }}
            >
              {statusLabel(messages, view.status)}
            </span>

            <Row label={messages.cabinet.order.shouldArrive} value={formatTime(view.promised_at)} />

            {view.eta_delivery_at === null ? null : (
              <Row label={messages.task.arrivalEta} value={formatTime(view.eta_delivery_at)} />
            )}

            {view.delivery_window_from === null ? null : (
              <Row
                label={messages.cabinet.create.deadline}
                value={`${formatTime(view.delivery_window_from)} — ${formatTime(view.delivery_window_to)}`}
              />
            )}

            {view.address_short === null ? null : (
              <Row label={messages.cabinet.create.address} value={view.address_short} />
            )}
          </section>

          {view.courier === null ? null : (
            <section
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-lg)',
                padding: 'var(--sp-4)',
                display: 'grid',
                gap: 'var(--sp-2)',
              }}
            >
              <Row label={messages.cabinet.order.driver} value={view.courier.name} />

              {view.courier.position_at === null ? null : (
                <Row
                  label={messages.cabinet.live.updatedAgo}
                  value={formatTime(view.courier.position_at)}
                />
              )}

              {/* Телефона курьера здесь нет и не будет: связь идёт через диспетчера. */}
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-faint)' }}>
                {messages.cabinet.order.driverPhoneNote}
              </span>
            </section>
          )}

          {view.courier?.position === null || view.courier === null ? null : (
            <p style={{ color: 'var(--warn)' }}>{messages.cabinet.live.mapUnavailable}</p>
          )}
        </>
      )}
    </main>
  );
}

function Row({ label, value }: { readonly label: string; readonly value: string }): ReactElement {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="num">{value}</span>
    </div>
  );
}
